/**
 * Employee PDF export — data schema (WHAT goes into the PDF).
 *
 * This module is the single source of truth for the export dialog AND the PDF
 * renderer: which sections exist, which fields belong to each, which fields
 * survive "Summary" mode, and which document images can be attached.
 * The renderer (`employeePdf.js`) only knows how to draw the generic blocks
 * emitted here, so adding/removing a field or section is a schema-only change.
 *
 * Block kinds a section builder may emit:
 *   { kind: 'kv',       pairs: [{label, value}], perRow }   label/value grid
 *   { kind: 'subtitle', text }                              small group heading
 *   { kind: 'entities', items: [{label, pairs, images?}] }  repeated sub-records
 *   { kind: 'images',   items: [{label, url}] }             document image grid
 *   { kind: 'note',     text }                              muted empty-state
 */
import dayjs from 'dayjs';

/* ------------------------------------------------------------------ */
/* Generic value helpers                                               */
/* ------------------------------------------------------------------ */
const fmtDate = (v) => {
    if (!v) return 'N/A';
    const d = dayjs(v);
    return d.isValid() ? d.format('DD MMM YYYY') : 'N/A';
};

const fmtYear = (v) => {
    if (!v) return 'N/A';
    const d = dayjs(v);
    return d.isValid() ? d.format('YYYY') : String(v);
};

const joinList = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : v);

const joinAddress = (a) =>
    a
        ? [a.address, a.thana, a.district, a.city, a.country]
              .filter(Boolean)
              .join(', ')
        : '';

const personName = (p) =>
    p
        ? [p.firstname, p.middlename, p.lastname]
              .filter(Boolean)
              .join(' ')
              .replace(/\s+/g, ' ')
              .trim()
        : '';

const yesNo = (v) => (v ? 'Yes' : 'No');

/* pair(label, value, includeInSummary) */
const pair = (label, value, s = false) => ({ label, value, s });

const kv = (pairs, perRow = 2) => ({
    kind: 'kv',
    pairs: pairs.filter(Boolean),
    perRow
});
const subtitle = (text) => ({ kind: 'subtitle', text });
const note = (text) => ({ kind: 'note', text });

/** In summary mode keep only the flagged pairs. */
const pick = (detailed, pairs) =>
    detailed ? pairs.filter(Boolean) : pairs.filter(Boolean).filter((p) => p.s);

/* ------------------------------------------------------------------ */
/* Employee record helpers (shared with the renderer)                  */
/* ------------------------------------------------------------------ */
export const fullNameOf = (e) =>
    personName(e) || e.user?.email || 'Employee';

export const initialsOf = (e) => {
    const parts = [e.firstname, e.lastname].filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    if (parts.length === 1) return parts[0][0].toUpperCase();
    const email = e.user?.email || '';
    return email ? email[0].toUpperCase() : '?';
};

/** Guarantee the nested collections the builders assume always exist. */
export const normalizeEmployee = (raw) => {
    const e = raw || {};
    const contact = { ...(e.contact || {}) };
    if (!contact.officialEmail && e.user?.email)
        contact.officialEmail = e.user.email;
    return {
        ...e,
        contact,
        family: e.family || {},
        identification: e.identification || {},
        academic: e.academic || {},
        workExperiences: Array.isArray(e.workExperiences)
            ? e.workExperiences
            : [],
        relatedEmployees: Array.isArray(e.relatedEmployees)
            ? e.relatedEmployees
            : [],
        nominees: Array.isArray(e.nominees) ? e.nominees : [],
        references: Array.isArray(e.references) ? e.references : [],
        insurances: Array.isArray(e.insurances) ? e.insurances : []
    };
};

/* ------------------------------------------------------------------ */
/* Document image parts (the toggleable attachment groups)             */
/* ------------------------------------------------------------------ */
export const DOCUMENT_PARTS = [
    { key: 'photograph', label: 'Photograph' },
    { key: 'signature', label: 'Signature' },
    { key: 'nid', label: 'NID card' },
    { key: 'etin', label: 'E-TIN document' },
    { key: 'nomineeDocs', label: 'Nominee photo & signature' },
    { key: 'insuranceDocs', label: 'Insurance photograph' }
];

/** Every image URL the chosen parts require (for pre-loading). */
export const collectDocumentUrls = (e, parts = {}) => {
    const urls = [];
    const id = e.identification || {};
    if (parts.photograph && id.photograph) urls.push(id.photograph);
    if (parts.signature && id.signature) urls.push(id.signature);
    if (parts.nid && id.nid) urls.push(id.nid);
    if (parts.etin && id.etin) urls.push(id.etin);
    if (parts.nomineeDocs)
        (e.nominees || []).forEach((n) => {
            if (n.photograph) urls.push(n.photograph);
            if (n.signature) urls.push(n.signature);
        });
    if (parts.insuranceDocs)
        (e.insurances || []).forEach((i) => {
            if (i.photograph) urls.push(i.photograph);
        });
    return [...new Set(urls)];
};

/* ------------------------------------------------------------------ */
/* Section builders                                                    */
/* ctx = { e, detailed, parts, calendarName }                          */
/* ------------------------------------------------------------------ */
const buildGeneral = ({ e, detailed, calendarName }) => {
    const pairs = [
        pair('Full Name', fullNameOf(e), true),
        pair('Name (In Bengali)', e.nameInBengali),
        pair('Initial', e.initial),
        pair('Gender', e.gender, true),
        pair('Date of Birth', fmtDate(e.dateOfBirth), true),
        pair('Place of Birth', e.placeOfBirth),
        pair('Blood Group', e.bloodGroup, true),
        pair('Religion', e.religion),
        pair('Marital Status', e.maritalStatus),
        pair('Employee ID', e.employeeId, true),
        pair('Joining Date', fmtDate(e.joiningDate), true),
        pair('Designation', e.designation?.name, true),
        pair('Department', e.department?.name, true),
        pair('Branch', e.branch?.name, true),
        pair('Employee Category', e.employeeCategory?.name),
        pair('Shift', e.shift?.name || e.shiftName),
        pair('Reporting Manager', e.reportingManager),
        pair('Work Calendar', calendarName || e.calenderId)
    ];
    if (e.resignationDate) {
        pairs.push(pair('Last Working Day', fmtDate(e.resignationDate), true));
        if (typeof e.isTerminated === 'boolean')
            pairs.push(pair('Terminated', yesNo(e.isTerminated)));
        if (e.description) pairs.push(pair('Resignation Note', e.description));
    }
    return [kv(pick(detailed, pairs))];
};

const buildContact = ({ e, detailed }) => {
    const c = e.contact;
    const emergency = c.emergencyContact || {};
    if (!detailed) {
        return [
            kv([
                pair('Official E-Mail', c.officialEmail, true),
                pair('Personal E-Mail', c.personalEmail, true),
                pair('Mobile No', c.mobileNumber, true),
                pair('Emergency Contact', emergency.contact, true)
            ])
        ];
    }
    return [
        subtitle('Personal'),
        kv([
            pair('Official E-Mail', c.officialEmail),
            pair('Personal E-Mail', c.personalEmail),
            pair('Mobile No', c.mobileNumber)
        ]),
        subtitle('Present Address'),
        kv(
            [
                pair('Address', joinAddress(c.presentAddress)),
                pair('Mobile No', c.presentAddress?.phoneNumber)
            ],
            1
        ),
        subtitle('Permanent Address'),
        kv(
            [
                pair('Address', joinAddress(c.permanentAddress)),
                pair('Mobile No', c.permanentAddress?.phoneNumber)
            ],
            1
        ),
        subtitle('Emergency Contact'),
        kv([
            pair('Name', personName(emergency)),
            pair('Mobile No', emergency.contact),
            pair('Relation with Employee', emergency.relation),
            pair('Address', emergency.address?.address)
        ])
    ];
};

const buildFamily = ({ e, detailed }) => {
    const f = e.family;
    const spouse = f.spouse || {};
    const children = Array.isArray(f.children) ? f.children : [];
    const relatives = e.relatedEmployees;

    if (!detailed) {
        return [
            kv([
                pair('Is Married', yesNo(f.isMarried), true),
                pair('Spouse Name', personName(spouse), true),
                pair('Children', String(children.length), true)
            ])
        ];
    }

    const blocks = [
        subtitle('Parents'),
        kv([
            pair("Father's Name", personName(e.father)),
            pair("Father's Name (In Bengali)", e.father?.nameInBengali),
            pair("Father's Occupation", e.father?.occupation),
            pair("Mother's Name", personName(e.mother)),
            pair("Mother's Name (In Bengali)", e.mother?.nameInBengali),
            pair("Mother's Occupation", e.mother?.occupation)
        ]),
        subtitle('Marital'),
        kv([
            pair('Is Married', yesNo(f.isMarried)),
            pair('Marriage Date', f.marriageDate ? fmtDate(f.marriageDate) : 'N/A'),
            pair('Spouse Name', personName(spouse)),
            pair('Spouse Contact', spouse.contact),
            pair('Spouse Occupation', spouse.occupation),
            pair(
                'Spouse Level of Education',
                spouse.education?.institutions?.[0]?.qualification
            )
        ])
    ];

    blocks.push(subtitle('Children'));
    if (children.length) {
        blocks.push({
            kind: 'entities',
            items: children.map((child, i) => ({
                label: `Child ${i + 1}`,
                pairs: [
                    pair('Name', personName(child) || child.firstname),
                    pair('Gender', child.gender)
                ]
            }))
        });
    } else {
        blocks.push(note('No children on record.'));
    }

    blocks.push(subtitle('Related Employees in EchoLogyx'));
    if (relatives.length) {
        blocks.push({
            kind: 'entities',
            items: relatives.map((r, i) => ({
                label: `Related Employee ${i + 1}`,
                pairs: [
                    pair('Employee ID', r.employeeId),
                    pair('Relation with Employee', r.relationship || r.relation)
                ]
            }))
        });
    } else {
        blocks.push(note('None on record.'));
    }

    return blocks;
};

const buildIdentification = ({ e, detailed, parts }) => {
    const id = e.identification;
    const passport = id.passport || {};
    const pairs = [
        pair('Nationality', id.nationality, true),
        pair('NID Number', id.nidNumber, true),
        pair('Tax Identification Number', id.tin),
        pair('Driving License Number', id.drivingLicenseNumber),
        pair('Passport Number', passport.number, true),
        pair('Passport Issue Place', passport.issuePlace),
        pair(
            'Passport Issue Date',
            passport.issueDate ? fmtDate(passport.issueDate) : 'N/A'
        ),
        pair(
            'Passport Expiry Date',
            passport.expiryDate ? fmtDate(passport.expiryDate) : 'N/A'
        )
    ];
    const blocks = [kv(pick(detailed, pairs))];

    const docs = [
        parts.photograph && id.photograph
            ? { label: 'Photograph', url: id.photograph }
            : null,
        parts.signature && id.signature
            ? { label: 'Signature', url: id.signature }
            : null,
        parts.nid && id.nid ? { label: 'NID Card', url: id.nid } : null,
        parts.etin && id.etin ? { label: 'E-TIN', url: id.etin } : null
    ].filter(Boolean);
    if (docs.length) {
        blocks.push(subtitle('Attached Documents'));
        blocks.push({ kind: 'images', items: docs });
    }
    return blocks;
};

const buildAcademic = ({ e, detailed }) => {
    const institutions = Array.isArray(e.academic?.institutions)
        ? e.academic.institutions
        : [];
    if (!institutions.length) return [note('No academic records.')];
    if (!detailed) {
        return [
            kv(
                institutions.map((ins, i) =>
                    pair(
                        `Qualification ${i + 1}`,
                        [
                            ins.qualification,
                            ins.name,
                            ins.passingYear ? `(${fmtYear(ins.passingYear)})` : ''
                        ]
                            .filter(Boolean)
                            .join(' - '),
                        true
                    )
                ),
                1
            )
        ];
    }
    return [
        {
            kind: 'entities',
            items: institutions.map((ins, i) => ({
                label: `Academic Qualification ${i + 1}`,
                pairs: [
                    pair('Qualification', ins.qualification),
                    pair('Discipline / Department', ins.department),
                    pair('Institute Name', ins.name),
                    pair('Institute / Board', ins.board),
                    pair('Passing Year', fmtYear(ins.passingYear)),
                    pair('Result (Marks/CGPA)', ins.result)
                ]
            }))
        }
    ];
};

const buildWorkExperience = ({ e, detailed }) => {
    const list = e.workExperiences;
    if (!list.length) return [note('No work experience records.')];
    if (!detailed) {
        return [
            kv(
                list.map((w, i) =>
                    pair(
                        `Experience ${i + 1}`,
                        [
                            w.designation,
                            w.companyName,
                            w.yearsOfExperience
                                ? `(${w.yearsOfExperience} yrs)`
                                : ''
                        ]
                            .filter(Boolean)
                            .join(' - '),
                        true
                    )
                ),
                1
            )
        ];
    }
    return [
        {
            kind: 'entities',
            items: list.map((w, i) => ({
                label: `Work Experience ${i + 1}`,
                pairs: [
                    pair('Company Name', w.companyName),
                    pair('Location', w.location),
                    pair('Designation', w.designation),
                    pair('Years of Experience', w.yearsOfExperience),
                    pair('Skills', joinList(w.skills)),
                    pair('Contributions', joinList(w.contributions)),
                    pair('Achievements', joinList(w.achievements))
                ]
            }))
        }
    ];
};

const buildBank = ({ e }) => {
    const bank = e.bank || {};
    if (!bank.name && !bank.branch && !bank.accountNumber)
        return [note('No bank account on record.')];
    return [
        kv([
            pair('Bank Name', bank.name, true),
            pair('Branch', bank.branch, true),
            pair('A/C No', bank.accountNumber, true)
        ])
    ];
};

const buildNominees = ({ e, detailed, parts }) => {
    const list = e.nominees;
    if (!list.length) return [note('No nominees on record.')];
    if (!detailed) {
        return [
            kv(
                list.map((n, i) =>
                    pair(
                        `Nominee ${i + 1}`,
                        [
                            n.information?.firstname,
                            n.information?.relation,
                            n.percentage ? `(${n.percentage}%)` : ''
                        ]
                            .filter(Boolean)
                            .join(' - '),
                        true
                    )
                ),
                1
            )
        ];
    }
    return [
        {
            kind: 'entities',
            items: list.map((n, i) => ({
                label: `Nominee ${i + 1}`,
                pairs: [
                    pair('Nomination Purpose', n.nominationPurpose),
                    pair(
                        'Nomination Date',
                        n.nominationDate ? fmtDate(n.nominationDate) : 'N/A'
                    ),
                    pair('Name of the Nominee', n.information?.firstname),
                    pair('Relation with Employee', n.information?.relation),
                    pair('Percentage Amount', n.percentage),
                    pair(
                        'Date of Birth',
                        n.dateOfBirth ? fmtDate(n.dateOfBirth) : 'N/A'
                    ),
                    pair('Occupation', n.information?.occupation),
                    pair('Address', n.information?.address?.address),
                    pair('Contact No', n.information?.contact),
                    pair('E-Mail', n.information?.email)
                ],
                images: parts.nomineeDocs
                    ? [
                          n.photograph
                              ? { label: 'Photograph', url: n.photograph }
                              : null,
                          n.signature
                              ? { label: 'Signature', url: n.signature }
                              : null
                      ].filter(Boolean)
                    : []
            }))
        }
    ];
};

const buildReferences = ({ e, detailed }) => {
    const list = e.references;
    if (!list.length) return [note('No references on record.')];
    if (!detailed) {
        return [
            kv(
                list.map((r, i) =>
                    pair(
                        `Reference ${i + 1}`,
                        [r.firstname, r.occupation].filter(Boolean).join(' - '),
                        true
                    )
                ),
                1
            )
        ];
    }
    return [
        {
            kind: 'entities',
            items: list.map((r, i) => ({
                label: `Reference ${i + 1}`,
                pairs: [
                    pair('Name', r.firstname),
                    pair('Occupation', r.occupation),
                    pair('Relation with Employee', r.relation),
                    pair('Contact No', r.contact),
                    pair('E-Mail', r.email)
                ]
            }))
        }
    ];
};

const buildInsurances = ({ e, detailed, parts }) => {
    const list = e.insurances;
    if (!list.length) return [note('No insurance records.')];
    if (!detailed) {
        return [
            kv(
                list.map((ins, i) =>
                    pair(
                        `Insurance ${i + 1}`,
                        [
                            ins.information?.firstname,
                            ins.natureOfInsurance,
                            ins.insuranceAmount ? `(${ins.insuranceAmount})` : ''
                        ]
                            .filter(Boolean)
                            .join(' - '),
                        true
                    )
                ),
                1
            )
        ];
    }
    return [
        {
            kind: 'entities',
            items: list.map((ins, i) => ({
                label: `Insurance ${i + 1}`,
                pairs: [
                    pair('Name of Family Member', ins.information?.firstname),
                    pair('Nature of Insurance', ins.natureOfInsurance),
                    pair('Insurance Amount', ins.insuranceAmount),
                    pair('Relation with Employee', ins.information?.relation),
                    pair(
                        'Registration Date',
                        ins.registrationDate
                            ? fmtDate(ins.registrationDate)
                            : 'N/A'
                    ),
                    pair(
                        'Date of Birth',
                        ins.dateOfBirth ? fmtDate(ins.dateOfBirth) : 'N/A'
                    ),
                    pair('Occupation', ins.information?.occupation)
                ],
                images:
                    parts.insuranceDocs && ins.photograph
                        ? [{ label: 'Photograph', url: ins.photograph }]
                        : []
            }))
        }
    ];
};

/* ------------------------------------------------------------------ */
/* Section registry (order = order in the PDF and in the dialog)       */
/* ------------------------------------------------------------------ */
export const EXPORT_SECTIONS = [
    { key: 'general', label: 'General Information', build: buildGeneral },
    { key: 'contact', label: 'Contact Information', build: buildContact },
    { key: 'family', label: 'Family Information', build: buildFamily },
    {
        key: 'identification',
        label: 'Identification',
        build: buildIdentification
    },
    {
        key: 'academic',
        label: 'Academic Qualifications',
        build: buildAcademic
    },
    {
        key: 'workExperience',
        label: 'Work Experience',
        build: buildWorkExperience
    },
    { key: 'bank', label: 'Bank Account Details', build: buildBank },
    { key: 'nominees', label: 'Nominee Information', build: buildNominees },
    { key: 'references', label: 'References', build: buildReferences },
    {
        key: 'insurances',
        label: 'Insurance Information',
        build: buildInsurances
    }
];

export const defaultSectionSelection = () =>
    EXPORT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: true }), {});

export const defaultImageParts = () =>
    DOCUMENT_PARTS.reduce((acc, p) => ({ ...acc, [p.key]: true }), {});
